import { PITCH, startKickoff, startRestart, type MatchState, type PlayerState, type SimEvent, type TeamIdx } from '@fafi/shared';
import { countEvents, throughGate, type LessonProp, type LessonSpec, type LessonStep, type Progress } from './lessons';

// D139 (§8.2) — the training ground, content wave 1.
//
// Design language, applied to every lesson below: SHOW THE KEYS (the `{key:…}` placeholders resolve
// against live bindings, so the copy is rebind-proof) · one skill per step · every step individually
// detectable and celebrated · attempts free, progress permanent · coach copy short, warm and football ·
// props (cones, gates, target zones) are RENDER-ONLY markers — objectives read positions and events, so
// the sim surface of the whole tutorial is exactly zero.

const PLAYER: TeamIdx = 0;
/** The entity a lesson hands the player. Slot 0 is the keeper (createMatchState), so 1 is his outfielder. */
const ME = 1;

const dirOf = (s: MatchState): number => s.attackDir[PLAYER]!;

export function clearPitch(s: MatchState, park = -60): void {
  for (const p of s.players) {
    p.pos = { x: park, y: 40 + p.id };
    p.vel = { x: 0, y: 0 };
    p.action = 'IDLE';
    p.actionTicks = 0;
    p.charge = null;
    p.sprinting = false;
  }
}

export function keepersHome(s: MatchState): void {
  const pitch = PITCH[s.mode.teamSize];
  for (const p of s.players) {
    if (!p.isGk) continue;
    const d = s.attackDir[p.team]!;
    p.pos = { x: (-d * pitch.length) / 2 + d * 0.4, y: 0 };
    p.vel = { x: 0, y: 0 };
  }
}

/**
 * D148 — spread bodies a set-piece lesson does not pin, deterministically and without stacking.
 *
 * `clearPitch` parks everyone off the pitch, which is fine when the lesson then places the two or three
 * players it cares about — at 5-a-side that left 8 spectators in a heap nobody looked at. On the full
 * pitch it leaves TWENTY, and "penalty, free kick and corner on the big field 11v11" is precisely a
 * request to see a real scene. Bodies fan across `y` at a fixed `x`, stepping back a row every five, so
 * no two share a position and the D143 bots have a legal shape to run from.
 */
function fanAcross(players: readonly PlayerState[], x: number, yHalf: number, rowStep: number): void {
  const PER_ROW = 5;
  players.forEach((p, i) => {
    const row = Math.floor(i / PER_ROW);
    const col = i % PER_ROW;
    const n = Math.min(PER_ROW, players.length - row * PER_ROW);
    p.pos = { x: x - Math.sign(x || 1) * row * rowStep, y: n === 1 ? 0 : ((col - (n - 1) / 2) / ((n - 1) / 2)) * yHalf };
    p.vel = { x: 0, y: 0 };
  });
}

/** The outfielders of `team` that a lesson has not already pinned (by entity id). */
const restOf = (s: MatchState, team: TeamIdx, pinned: readonly number[]): PlayerState[] =>
  s.players.filter((p) => p.team === team && !p.isGk && !pinned.includes(p.id));

/**
 * Put `ME` at `(x, y)` with the ball `gap` metres ahead of him, and open the phase — a lesson starts in
 * play, not at a whistle.
 *
 * D154: `gap` used to be a fixed 1.2 m, which is inside the claim's reach once you account for the
 * carry point, so `first-touch` handed you the ball before you pressed anything and its opening step
 * ("Walk onto the ball to take it") ticked itself off. A lesson whose first instruction completes
 * without you teaches you that the checklist is lying.
 */
export function ballToMe(s: MatchState, x: number, y: number, gap = 1.2): void {
  const me = s.players[ME]!;
  me.pos = { x, y };
  me.facing = dirOf(s) > 0 ? 0 : Math.PI;
  s.phase = 'PLAY';
  s.restart = null;
  s.ball.possessor = null;
  s.ball.pos = { x: x + dirOf(s) * gap, y, z: 0 };
  s.ball.vel = { x: 0, y: 0, z: 0 };
  s.ball.claimCooldownTicks = 0;
  s.ball.claimCooldownOnly = null;
  s.ball.lastTouch = null;
  s.ball.kickedBy = null;
}

const step = (
  id: string,
  instruction: string,
  detect: LessonStep['goal']['detect'],
  praise: readonly string[],
  count?: number,
  failOn?: LessonStep['failOn'],
): LessonStep => ({
  id,
  instruction,
  goal: count === undefined ? { detect } : { count, detect },
  praise,
  ...(failOn ? { failOn } : {}),
});

/**
 * D151 (playtest 6, reports 3 + 5) — a detector is about YOU, not about your shirt.
 *
 * These helpers were team-scoped (`mineIds`), so any bot on your side completed your step for you. In
 * `passing-clinic` that meant the last two objectives ticked themselves off the moment a bot passed to
 * another bot — the reported *"it marks the 2 last todos complete even though i only played long
 * passes"*. `iSaved` was worse than team-scoped: it counted **any** SAVE in the match, including the
 * opposition keeper's.
 *
 * Every one of them now takes the `seat` — the entity the human is actually driving, which since D143
 * is not always entity 1 (keeper-week seats you at 0).
 */

/** I took possession (any reason). */
export const iGotIt = (events: readonly SimEvent[], _s: MatchState, memo: unknown, seat: number): Progress =>
  countEvents(events, memo, (e) => e.t === 'POSSESSION' && e.entity === seat);

/** I kicked it — a real deliberate kick, not a dribble touch. */
export const iKicked = (events: readonly SimEvent[], s: MatchState, memo: unknown, seat: number): Progress =>
  countEvents(events, memo, (e) => e.t === 'POSSESSION' && e.entity === null && e.why === 'KICK' && s.ball.kickedBy === seat);

/**
 * A pass of MINE found a team-mate. Both halves matter: the receiver is someone else on my side, and
 * the ball got to him because **I** kicked it. Without the second half a bot-to-bot pass counted as my
 * completed objective, which is report 3 exactly.
 */
export const mateReceived = (events: readonly SimEvent[], s: MatchState, memo: unknown, seat: number): Progress =>
  countEvents(events, memo, (e) => {
    if (e.t !== 'POSSESSION' || e.entity === null || e.entity === seat) return false;
    const me = s.players[seat];
    return me !== undefined && s.players[e.entity]?.team === me.team && s.ball.kickedBy === seat;
  });

/** I scored — not "my team scored". */
export const iScored = (events: readonly SimEvent[], s: MatchState, memo: unknown, seat: number): Progress =>
  countEvents(events, memo, (e) => e.t === 'GOAL' && !e.ownGoal && e.scorer === seat && s.players[seat] !== undefined);

/** I made the save — the opposition keeper's do not count toward a keeping lesson. */
export const iSaved = (events: readonly SimEvent[], _s: MatchState, memo: unknown, seat: number): Progress =>
  countEvents(events, memo, (e) => e.t === 'SAVE' && e.keeper === seat);

/**
 * D153 — failure conditions. A rehearsal that carries on after you have failed is not a rehearsal; it
 * is a match you did not ask for. *"In penalty school, a full game plays after i miss the penalty."*
 */

/** Anyone in the other shirt has the ball now. */
const oppTookIt = (e: readonly SimEvent[], s: MatchState, seat: number): boolean => {
  const me = s.players[seat];
  if (me === undefined) return false;
  return e.some((ev) => ev.t === 'POSSESSION' && ev.entity !== null && s.players[ev.entity] !== undefined && s.players[ev.entity]!.team !== me.team);
};

/** The set piece is spent: saved, put out, or gathered by the other side. */
const chanceGone = (e: readonly SimEvent[], s: MatchState, seat: number): boolean =>
  e.some((ev) => ev.t === 'SAVE') || e.some((ev) => ev.t === 'OUT_OF_PLAY') || oppTookIt(e, s, seat);

/** He has got past me, carrying it, on my side of the ball. */
const oppPastMe = (_e: readonly SimEvent[], s: MatchState, seat: number): boolean => {
  const me = s.players[seat];
  const carrier = s.ball.possessor !== null ? s.players[s.ball.possessor] : undefined;
  if (me === undefined || carrier === undefined || carrier.team === me.team) return false;
  return (carrier.pos.x - me.pos.x) * s.attackDir[me.team]! < -1.5; // behind me, heading for my goal
};

const GATE_R = 1.8;

export const LESSONS: readonly LessonSpec[] = [
  // 1 ─────────────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'first-touch',
    title: 'First Touch',
    blurb: 'An empty pitch, one ball, one goal. Everything starts here.',
    icon: '⚽',
    // D154: was 5-a-side, where the last gate sat 6 m off the goal line — "the last cones are too close
    // to the goal". On the full pitch the same route finishes ~18 m out, which is a shooting position.
    teamSize: 11,
    bots: 'defense',
    playerTeam: PLAYER,
    resetOn: 'outcome',
    props: [
      { kind: 'gate', x: 18, y: -4, r: GATE_R },
      { kind: 'gate', x: 26, y: 4, r: GATE_R },
      { kind: 'gate', x: 34, y: 0, r: GATE_R },
    ],
    setup: (s) => {
      clearPitch(s);
      keepersHome(s);
      ballToMe(s, 6, 0, 4); // four metres off it: walking onto the ball is the first thing you do
    },
    steps: [
      step('walk-up', 'Walk onto the ball with {key:moveUp}{key:moveLeft}{key:moveDown}{key:moveRight} to take it.', iGotIt, ['That is yours now.', 'Good — feel where it sits.']),
      step('gates', 'Dribble through the two orange cones with {key:moveUp}{key:moveLeft}{key:moveDown}{key:moveRight}. Steady — no {key:sprint} yet.', (_e, st, memo, seat) => throughGate(st, seat, { kind: 'gate', x: 18, y: -4, r: GATE_R }, memo), ['Lovely touch.', 'Nice and tight.']),
      step('sprint-gate', 'Now hold {key:sprint} and take the last gate at pace.', (_e, st, memo, seat) => throughGate(st, seat, { kind: 'gate', x: 34, y: 0, r: GATE_R }, memo), ['That is the difference — pace with the ball still yours.']),
      step('score', 'Finish it. {key:shoot} — hold to aim, let go to hit it.', iScored, ['THAT is the one!', 'Buried it.']),
    ],
    bonus: step('bonus-clean', '★ Score with {key:shoot} without losing the ball once.', (e, _st, memo, seat) => {
      const lost = (typeof memo === 'object' && memo !== null ? (memo as { lost?: boolean }).lost : false) === true;
      const nowLost = lost || e.some((ev) => ev.t === 'POSSESSION' && ev.entity === null && ev.why !== 'KICK');
      const scored = e.some((ev) => ev.t === 'GOAL' && !ev.ownGoal && ev.scorer === seat); // D151: MY goal
      return { done: scored && !nowLost ? 1 : 0, memo: { lost: nowLost } };
    }, ['★ Sharp.']),
  },

  // 2 ─────────────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'passing-clinic',
    title: 'Passing Clinic',
    blurb: 'Three mates in a rondo shape. Find them.',
    icon: '🎯',
    teamSize: 5,
    bots: 'defense',
    playerTeam: PLAYER,
    setup: (s) => {
      clearPitch(s);
      keepersHome(s);
      const d = dirOf(s);
      const mates = s.players.filter((p) => p.team === PLAYER && !p.isGk && p.id !== ME);
      const spots = [
        { x: 8, y: -9 },
        { x: 8, y: 9 },
        { x: 18, y: 0 },
      ];
      mates.forEach((m, i) => {
        const sp = spots[i % spots.length]!;
        m.pos = { x: d * sp.x, y: sp.y };
      });
      ballToMe(s, -d * 2, 0);
    },
    steps: [
      step('short', 'Three passes to feet — {key:passDirect}.', mateReceived, ['Good weight.', 'Straight to his laces.', 'Again — same again.'], 3),
      step('long', 'Two long diagonals now — {key:passLong}.', mateReceived, ['That switches the play.', 'He did not have to break stride.'], 2),
      step('lofted', 'Hold {key:loft} and play one over the top.', iKicked, ['Up and over. Lovely.']),
      step('call', 'Ask for it back — {key:call} — and take the return.', iGotIt, ['That is a conversation, not a pass.']),
    ],
  },

  // 3 ─────────────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'beat-your-man',
    title: 'Beat Your Man',
    blurb: 'One against one, both ways. Get past him, then stop him.',
    icon: '🌀',
    teamSize: 3,
    bots: 'both',
    playerTeam: PLAYER,
    props: [
      { kind: 'gate', x: 14, y: -6, r: GATE_R },
      { kind: 'gate', x: 14, y: 6, r: GATE_R },
    ],
    setup: (s) => {
      clearPitch(s);
      keepersHome(s);
      const d = dirOf(s);
      const opp = s.players.find((p) => p.team !== PLAYER && !p.isGk)!;
      opp.pos = { x: d * 5, y: 0 };
      ballToMe(s, -d * 2, 0);
    },
    steps: [
      step('past-1', 'Take him on. Through either gate — {key:shield} protects it while you turn.', (_e, st, memo, seat) => {
        const a = throughGate(st, seat, { kind: 'gate', x: 14, y: -6, r: GATE_R }, memo);
        return a.done > 0 ? a : throughGate(st, seat, { kind: 'gate', x: 14, y: 6, r: GATE_R }, memo);
      }, ['Gone past him.', 'He never got near it.'], 2, oppTookIt), // D153: lose it and the rep is over
      step('win-it', 'Now defend. Win it back twice — {key:tackle} is safe, {key:tackleHard} wins through contact.', iGotIt, ['Read that beautifully.', 'That is the one — timing, not lunging.'], 2, oppPastMe), // D153: he beats you, start again
    ],
  },

  // 4 ─────────────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'penalty-school',
    title: 'Penalty School',
    blurb: 'Five balls, one keeper. Pick your corner and stick to it.',
    icon: '🥅',
    teamSize: 11, // D148 (playtest 5, report 11): a set piece belongs on the full pitch
    bots: 'both',
    playerTeam: PLAYER,
    resetOn: 'outcome',
    setup: (s: MatchState, cfg) => {
      clearPitch(s);
      keepersHome(s);
      const pitch = PITCH[s.mode.teamSize];
      const spot = { x: dirOf(s) * (pitch.length / 2 - pitch.penaltySpotDist), y: 0 };
      // D148: at a real penalty everyone else waits OUTSIDE the box — two ranks on the edge of the D
      fanAcross(restOf(s, PLAYER, [ME]), dirOf(s) * (pitch.length / 2 - pitch.penaltyBoxDepth - 4), pitch.width * 0.3, 5);
      fanAcross(restOf(s, PLAYER === 0 ? 1 : 0, []), dirOf(s) * (pitch.length / 2 - pitch.penaltyBoxDepth - 8), pitch.width * 0.3, 5);
      // `restartTaker` picks the NEAREST outfielder, so the human has to be standing on it — otherwise
      // a fanned-out teammate takes his penalty and he watches. (Was invisible at 5-a-side, where
      // clearPitch parked everyone equally far away and the id tie-break happened to land on ME.)
      s.players[ME]!.pos = { x: spot.x, y: spot.y };
      startRestart(s, [], cfg, 'PENALTY', PLAYER, spot);
    },
    steps: [
      // D153: a saved or missed penalty serves the next ball — it does not become a match
      step('one-each-side', 'Hold {key:shoot} to aim — put one either side of him.', iScored, ['Never had a chance.', 'Other corner now.'], 2, chanceGone),
      step('rack', 'Three of five. Hold {key:shoot} to aim, release to strike — same routine every time.', iScored, ['That is a penalty taker.'], 3, chanceGone),
    ],
    bonus: step('bonus-corner', '★ Hold {key:shoot}, aim high into a corner, and release.', (e, s, memo, seat) => {
      const pitch = PITCH[s.mode.teamSize];
      const high = s.ball.pos.z > pitch.barH * 0.55 && Math.abs(s.ball.pos.y) > pitch.goalWidth * 0.28;
      const prev = (typeof memo === 'object' && memo !== null ? (memo as { hi?: boolean }).hi : false) === true;
      const scored = e.some((ev) => ev.t === 'GOAL' && !ev.ownGoal && ev.scorer === seat); // D151: MY goal
      return { done: scored && (prev || high) ? 1 : 0, memo: { hi: prev || high } };
    }, ['★ Top bins.']),
  },

  // 5 ─────────────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'free-kick-range',
    title: 'Free-Kick Range',
    blurb: 'A wall and a keeper. Over it from the middle, around it from wide.',
    icon: '🧱',
    teamSize: 11, // D148 (playtest 5, report 11): a set piece belongs on the full pitch
    bots: 'both',
    playerTeam: PLAYER,
    resetOn: 'outcome',
    setup: (s: MatchState, cfg) => {
      clearPitch(s);
      keepersHome(s);
      const pitch = PITCH[s.mode.teamSize];
      // the defenders share ONE anchor on the ball→goal line: `ai/wall.ts` is what turns them into a
      // wall, and D143 is what lets them actually walk there
      for (const p of s.players) if (p.team !== PLAYER && !p.isGk) p.pos = { x: dirOf(s) * (pitch.length / 2 - 12), y: 0 };
      // D148: and my side gives him something to aim at — runners across the box
      fanAcross(restOf(s, PLAYER, [ME]), dirOf(s) * (pitch.length / 2 - 8), pitch.width * 0.26, 4);
      const fk = { x: dirOf(s) * (pitch.length / 2 - 16), y: 0 };
      s.players[ME]!.pos = { x: fk.x, y: fk.y }; // he is the taker: `restartTaker` picks the nearest
      startRestart(s, [], cfg, 'FREE_KICK', PLAYER, fk);
    },
    steps: [
      step('direct', 'Beat the wall from here. {key:shoot}.', iScored, ['Over and down. That is the shot.']),
      step('cross', 'Now from wide: hold {key:loft} and {key:passLong} onto the runner.', mateReceived, ['Perfect ball in.']),
    ],
    bonus: step('bonus-first-time', '★ Press {key:shoot} while the cross is still in the air, and convert it first time.', iScored, ['★ On the volley. Outstanding.']),
  },

  // 6 ─────────────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'corner-craft',
    title: 'Corner Craft',
    blurb: 'From the flag, again and again, until the delivery is yours.',
    icon: '🚩',
    teamSize: 11, // D148 (playtest 5, report 11): a set piece belongs on the full pitch
    bots: 'both',
    playerTeam: PLAYER,
    resetOn: 'outcome',
    setup: (s: MatchState, cfg) => {
      clearPitch(s);
      keepersHome(s);
      const pitch = PITCH[s.mode.teamSize];
      const d = dirOf(s);
      // D148: `p.id % 3` gave three columns, which stacked 10 bodies at 11v11. Fan them instead.
      fanAcross(restOf(s, PLAYER, [ME]), d * (pitch.length / 2 - 6), pitch.goalWidth * 1.6, 4);
      fanAcross(restOf(s, PLAYER === 0 ? 1 : 0, []), d * (pitch.length / 2 - 5), pitch.goalWidth * 1.3, 4);
      const flag = { x: (d * pitch.length) / 2, y: pitch.width / 2 };
      s.players[ME]!.pos = { x: flag.x, y: flag.y }; // he takes the corner: nearest wins
      startRestart(s, [], cfg, 'CORNER', PLAYER, flag);
    },
    steps: [
      step('near', 'Near post first — hold {key:loft} and whip it in with {key:passLong}.', iKicked, ['That is the near-post ball.']),
      step('far', 'Now hang one up at the far post: hold {key:loft} and press {key:passLong}.', iKicked, ['He has to come for that.']),
      step('convert', 'Attack it yourself — {key:shoot} while it is in the air.', iScored, ['Header, goal, done.']),
    ],
  },

  // 7 ─────────────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'volley-finish',
    title: 'The Volley',
    blurb: 'Crosses arrive on a loop. Declare the finish and hit it.',
    icon: '💥',
    teamSize: 5,
    bots: 'both',
    playerTeam: PLAYER,
    resetOn: 'outcome',
    setup: (s) => {
      clearPitch(s);
      keepersHome(s);
      const d = dirOf(s);
      const pitch = PITCH[s.mode.teamSize];
      const me = s.players[ME]!;
      me.pos = { x: d * (pitch.length / 2 - 9), y: 0 };
      me.facing = d > 0 ? 0 : Math.PI;
      s.phase = 'PLAY';
      s.restart = null;
      // a delivery already in the air, dropping onto him
      s.ball.possessor = null;
      s.ball.pos = { x: d * (pitch.length / 2 - 16), y: -8, z: 2.6 };
      s.ball.vel = { x: d * 6, y: 6.5, z: 1.2 };
      s.ball.lastTouch = { entity: 2, team: PLAYER, tick: s.tick };
      s.ball.kickedBy = 2;
    },
    steps: [
      step('arm', 'Press {key:shoot} while the ball is still flying — that declares the finish.', iKicked, ['Declared it early. That is the trick.']),
      step('connect', 'Now connect: press {key:shoot} as it drops onto you.', iKicked, ['Clean contact.']),
      step('score-2', 'Two goals in the air with {key:shoot} — volley or header, both count.', iScored, ['Unstoppable.', 'That is a finish.'], 2),
    ],
    bonus: step('bonus-no-bounce', '★ Press {key:shoot} early and score one without letting it bounce.', iScored, ['★ Straight out of the air.']),
  },

  // 8 ─────────────────────────────────────────────────────────────────────────────────────────────
  {
    id: 'keeper-week',
    title: 'Keeper Week',
    blurb: 'You are the keeper. Line, hands, and the ball back out.',
    icon: '🧤',
    teamSize: 5,
    bots: 'both',
    playerTeam: PLAYER,
    playerRole: 'gk', // D143: "You are the keeper" — say so, and the router seats you there and locks it
    botsShoot: true, // D152: the one lesson where a bot MUST shoot — it is a penalty at your goal
    resetOn: 'outcome',
    setup: (s: MatchState, cfg) => {
      clearPitch(s);
      keepersHome(s);
      const pitch = PITCH[s.mode.teamSize];
      // the OPPOSITION takes a penalty at us — the one manual-dive moment
      startRestart(s, [], cfg, 'PENALTY', PLAYER === 0 ? 1 : 0, { x: -dirOf(s) * (pitch.length / 2 - pitch.penaltySpotDist), y: 0 });
    },
    steps: [
      step('line', 'Hold your line with {key:moveUp}{key:moveDown} — the automatic saves do the rest.', iSaved, ['Hands like that keep you in games.']),
      step('penalty', 'A penalty. {key:gkDiveL} and {key:gkDiveR} are yours — only here.', iSaved, ['SAVED. Go on, keeper.']),
      step('distribute', 'Now find the OPEN man — aim, then {key:passDirect}. The ring shows who is free.', mateReceived, ['That is how a move starts.']),
      step('punt', 'Under pressure with nobody free? Punt it to the safe flank with {key:passLong}.', iKicked, ['Right decision. Live to fight on.']),
    ],
  },
];

export function lessonById(id: string): LessonSpec | null {
  return LESSONS.find((l) => l.id === id) ?? null;
}

/** D138: the HOME badge — shipped lessons minus completed ones, never negative, never NaN. */
export function newLessonCount(completed: Readonly<Record<string, unknown>>): number {
  let done = 0;
  for (const l of LESSONS) if (Object.prototype.hasOwnProperty.call(completed, l.id)) done += 1;
  return Math.max(0, LESSONS.length - done);
}

/** Lesson ids this build ships — the ONLY keys a stored or server-sent progress blob may carry. */
export const LESSON_IDS: readonly string[] = LESSONS.map((l) => l.id);

export type { LessonProp };
export { startKickoff };
